'use client';

import clsx from 'clsx';
import type { ChangeEvent, KeyboardEvent } from 'react';
import React, { useRef, useState } from 'react';
import { BaseButton } from '@/app/components/buttons/BaseButton';
import { Input } from '@/app/components/form/Input';
import { useGetResendTimer } from '@/hooks/useGetResendTimer';
import { CardDetailsRequestMethod } from '../../consts/client-detail';
import type { RequestCardDetailsMethodType } from '../../types';

interface EnterVerificationCodeModalProps {
  onResendCode: () => void;
  selectedMethod: RequestCardDetailsMethodType;
  onVerify: (code: string) => void;
  attemptCount?: number | null;
  renderSubTitle: () => React.ReactNode;
  clearOtpError?: () => void;
  onChangeMethod?: (method: RequestCardDetailsMethodType) => void;
  verificationError: string | null;
  disableVerify?: boolean;
  isVerifyCodePending: boolean;
  hasVerifiedPhone?: boolean;
  hasAlternativeMethod?: boolean;
  onSendRequestWithMethod?: (method: RequestCardDetailsMethodType) => void;
}

const OTP_LENGTH = 6;

export const EnterVerificationCodeModal: React.FC<EnterVerificationCodeModalProps> = ({
  onResendCode,
  onChangeMethod,
  clearOtpError,
  selectedMethod,
  attemptCount,
  onVerify,
  isVerifyCodePending,
  hasAlternativeMethod = true,
  verificationError,
  disableVerify = false,
  renderSubTitle,
  hasVerifiedPhone,
  onSendRequestWithMethod,
}) => {
  const [otp, setOtp] = useState<string[]>(new Array(OTP_LENGTH).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>(new Array(OTP_LENGTH).fill(null));
  const { timer, isExpired, resetTimer } = useGetResendTimer();

  const handleResendCode = () => {
    resetTimer();
    onResendCode();
    setOtp(new Array(OTP_LENGTH).fill(''));
    inputRefs.current[0]?.focus();
  };

  const handleChangeMethod = (newMethod: RequestCardDetailsMethodType) => {
    onChangeMethod?.(newMethod);
    setOtp(new Array(OTP_LENGTH).fill(''));
    inputRefs.current[0]?.focus();
    if (onSendRequestWithMethod) {
      resetTimer();
      onSendRequestWithMethod(newMethod);
    }
  };

  const handleChange = (elementIndex: number, event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (/^\d?$/.test(value)) {
      clearOtpError?.();
      const newOtp = [...otp];
      newOtp[elementIndex] = value;
      setOtp(newOtp);

      if (value && elementIndex < OTP_LENGTH - 1) {
        inputRefs.current[elementIndex + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (elementIndex: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace') {
      clearOtpError?.();
      const newOtp = [...otp];
      if (newOtp[elementIndex]) {
        newOtp[elementIndex] = '';
        setOtp(newOtp);
      } else if (elementIndex > 0) {
        inputRefs.current[elementIndex - 1]?.focus();
      }
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>, pasteStartIndex: number) => {
    clearOtpError?.();
    event.preventDefault();
    const pasteData = event.clipboardData.getData('text').replace(/[^\d]/g, '').slice(0, OTP_LENGTH);

    if (pasteData) {
      const newOtp = [...otp];
      let currentOtpIndex = pasteStartIndex;
      let pasteDataIndex = 0;

      while (currentOtpIndex < OTP_LENGTH && pasteDataIndex < pasteData.length) {
        newOtp[currentOtpIndex] = pasteData[pasteDataIndex];
        currentOtpIndex++;
        pasteDataIndex++;
      }
      setOtp(newOtp);

      const lastFilledIndex = Math.min(pasteStartIndex + pasteData.length - 1, OTP_LENGTH - 1);
      if (lastFilledIndex < OTP_LENGTH - 1 && pasteData.length > 0) {
        inputRefs.current[lastFilledIndex + 1]?.focus();
      } else {
        inputRefs.current[lastFilledIndex]?.focus();
      }
    }
  };

  const finalCode = otp.join('');
  const isVerifyDisabled = finalCode.length !== OTP_LENGTH || !otp.every((digit) => /^\d$/.test(digit));

  const handleVerify = () => {
    if (!isVerifyDisabled) {
      onVerify(finalCode);
    }
  };

  return (
    <div className="px-4 py-6 md:px-[36px] md:py-[46px] flex flex-col items-center gap-[6px] text-center w-full max-w-[85vw] md:max-w-[548px]">
      <h2 className="light text-blue-900">Enter verification code</h2>
      <p className="md:text-body-regular text-body-small text-grey-700 max-w-[404px]">{renderSubTitle()}</p>
      {attemptCount && (
        <p className="md:text-body-regular text-body-small text-center text-blue-900 mt-[6px]">
          {`You have ${attemptCount} attempts left.`}
        </p>
      )}

      <div className="flex justify-center gap-2 md:gap-3 md:mt-[18px] mt-3">
        {otp.map((digit, index) => (
          <Input
            key={index}
            id={`otp-input-${index}`}
            name={`otp-input-name-${index}`}
            otpError={Boolean(verificationError)}
            type="text"
            value={digit}
            onChange={(e) => handleChange(index, e)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={(e) => handlePaste(e, index)}
            ref={(el) => {
              inputRefs.current[index] = el;
            }}
            maxLength={1}
            intent="otp"
          />
        ))}
      </div>

      <div className={clsx('flex gap-2 mt-[6px] items-center justify-start w-full')}>
        {verificationError ? (
          <p className="md:text-body-regular text-body-small text-red-500 text-left">{verificationError}</p>
        ) : (
          <p className="md:text-body-regular text-body-small text-black">Didn&apos;t receive a code?</p>
        )}
        {isExpired ? (
          <button onClick={handleResendCode} className="text-button-semibold text-blue-300 cursor-pointer">
            Resend code
          </button>
        ) : (
          <p className="md:text-button-semibold text-body-small font-semibold text-grey-700 whitespace-nowrap max-w-[120px]">
            Resend code in 00:{`${timer < 10 ? `0${timer}` : timer}`}
          </p>
        )}
      </div>

      <BaseButton
        intent="primary"
        onClick={handleVerify}
        size="full"
        className="md:mt-[30px] mt-3 w-full"
        disabled={
          isVerifyDisabled ||
          isVerifyCodePending ||
          disableVerify ||
          Boolean(attemptCount && Number(attemptCount) === 0)
        }
      >
        {isVerifyCodePending ? 'Verifying...' : 'Verify'}
      </BaseButton>
      {selectedMethod === CardDetailsRequestMethod.EMAIL && hasVerifiedPhone && hasAlternativeMethod && (
        <BaseButton
          className="md:mt-[18px] mt-1 !text-[16px]"
          intent="ghost_semibold"
          onClick={() => handleChangeMethod(CardDetailsRequestMethod.SMS)}
          size="medium"
          disabled={!isExpired}
        >
          Use mobile phone number instead
        </BaseButton>
      )}
      {selectedMethod === CardDetailsRequestMethod.SMS && hasAlternativeMethod && (
        <BaseButton
          className="mt-[18px] !text-[16px]"
          intent="ghost_semibold"
          onClick={() => handleChangeMethod(CardDetailsRequestMethod.EMAIL)}
          size="medium"
          disabled={!isExpired}
        >
          Use email instead
        </BaseButton>
      )}
    </div>
  );
};